import { StorageHelper } from "../../utils/StorageHelper";
import {
  AppStateResidents,
  FiltersType,
  IMAGE_STUB,
  MoveResidentWithDevicesBySocketDTO,
  presenceStatuses,
  Resident,
  ResidentMap,
  ResidentSortType,
} from ".";
import {
  ADDDeviceResidentDTO,
  DeviceResidentDTO,
  DeviceType,
} from "../devices";
import { AlertActionType, ALERTS } from "../alerts/types";
import { SortDirection } from "../types";
import {
  IAddDeviceToResidentBySocket,
  IBatchDisconectAndChangeResidentsSuccess,
  IDeleteBatchDevicesFromResidentsSuccess,
  IDeleteManyResidentsSuccess,
  IGetOneResidentSuccess,
  IGetResidentsSuccess,
  ISetResidentsSettings,
  ResidentActionTypes,
  RESIDENTS,
} from "./types";
import { HEALTH_CHECK } from "../healthCheck/types";

const initialState: AppStateResidents = {
  isLoading: undefined,
  isPerformingUserAction: false,
  residents: {},
  settings: {
    searchValue: "",
    sort: { type: ResidentSortType.RoomBed, direction: SortDirection.ASC },
    selectedResidents: [],
    showDischarged: false,
    filters: {
      [FiltersType.RESIDENTS_ACTIVE]: false,
      [FiltersType.RESIDENTS_AWAY]: false,
      [FiltersType.RESIDENTS_DISCHARGED]: false,
      [FiltersType.DEVICE_VITALS]: false,
      [FiltersType.DEVICE_BIOPATCH]: false,
      [FiltersType.DEVICE_WATCH]: false,
      [FiltersType.DEVICE_NO]: false,
      [FiltersType.DEVICE_STATUS_ACTIVE]: false,
      [FiltersType.DEVICE_STATUS_NOT_ACTIVE]: false,
      [FiltersType.UNPLUGGED]: false,
      [FiltersType.TURN_OFF]: false,
      [FiltersType.BatteryLevelLow]: false,
      [FiltersType.BatteryLevelEmpty]: false,
      [FiltersType.MAINTENANCE]: false,
      [FiltersType.LOW_WIFI]: false,
      [FiltersType.NO_WIFI]: false,
      [FiltersType.NO_BLUETOOTH]: false,
      [FiltersType.USER_NEARBY_IN_BED]: false,
      [FiltersType.USER_NEARBY_OUT_BED]: false,
      [FiltersType.USER_NEARBY_ROOM]: false,
    },
  },
};

const reducer = (
  state = initialState,
  action: ResidentActionTypes | AlertActionType
): AppStateResidents => {
  switch (action.type) {
    case HEALTH_CHECK.NEED_TO_NOTIFY_LOGIN: {
      return initialState;
    }
    case RESIDENTS.GET_RESIDENTS_SUCCESS: {
      const items = (action as IGetResidentsSuccess).payload;
      const mapedResidents = { ...state.residents } as ResidentMap;

      if (Array.isArray(items) && items.length > 0) {
        items.map((el: any) => {
          const { node, self, devices, picture, ...rest } = el;
          const resident: Resident = {
            ...rest,
            picture: picture ? picture : "",
          };
          resident.nodeId = Number.parseInt(node.id);
          resident.id = self.id;
          resident.devices = parseDevices(devices.sort(vitalsFirst));
          resident.lastUpdate =
            el?.lastNodePresenceUpdatingTime || el?.lastUpdate || undefined;
          resident.devicesMinimized = parseDevicesMap(
            devices.sort(vitalsFirst)
          );

          mapedResidents[resident.id] = resident;
          return null;
        });
        const residentSelect = StorageHelper.instance.GetResident();

        if (Object.keys(mapedResidents).indexOf(residentSelect) === -1) {
          StorageHelper.instance.ClearResident();
          StorageHelper.instance.ClearPage();
        }
        return {
          ...state,
          residents: mapedResidents,
          isLoading: false,
          settings: {
            ...state.settings,
          },
        };
      }
      return { ...state, isLoading: false };
    }

    case RESIDENTS.ADD_RESIDENT_BY_SOCKET: {
      const newResident = action.payload as any;
      const resident = {
        rules: [],
        statuses: [],
        devices: [],
        devicesMinimized: {},
        ...newResident,
        alerts: [],
        nodeId: Number.parseInt(newResident.node.id),
        id: newResident.self.id,
      } as Resident;
      console.log(
        "add resident ",
        newResident.self.id,
        " from reducer",
        " node",
        Number.parseInt(newResident.node.id)
      );
      const { devices, gender, picture } = newResident;
      const nodeStatus = resident?.nodePresence as
        | presenceStatuses
        | presenceStatuses.active;
      resident.nodePresence = nodeStatus.toLowerCase() as presenceStatuses;
      resident.lastUpdate = new Date().toISOString();
      // resident.statuses = parseStatuses(statuses);
      resident.devices = devices ? parseDevices(devices) : [];
      resident.devicesMinimized = devices ? parseDevicesMap(devices) : {};
      // resident.rules = [...parseRules(rules)];
      resident.gender = gender.slice(0, 1).toLowerCase() + gender.slice(1);
      resident.picture = picture ? picture : "";
      return {
        ...state,
        residents: { ...state.residents, [resident.id]: { ...resident } },
      };
    }

    case RESIDENTS.UPDATE_RESIDENT_BY_SOCKET: {
      const data = action.payload as any;
      const resident = state.residents[data?.self?.id];
      if (resident) {
        data.alerts = data.alerts || [];
        data.devices = data.devices || [];
        data.rules = data.rules || {};
        data.statuses = data.statuses || [];
        data.vitals = data.vitals || [];
        const {
          alerts,
          devices,
          vitals,
          self,
          node,
          rules,
          statuses,
          picture,
          ...rest
        } = data;

        return {
          ...state,
          residents: {
            ...state.residents,
            [resident.id]: {
              ...resident,
              ...rest,
              picture: picture ? picture : "",
              nodeId: Number.parseInt(node.id),
              devices: parseDevices(devices),
              lastUpdate:
                data?.lastNodePresenceUpdatingTime ||
                data?.lastUpdate ||
                undefined,
            },
          },
        };
      }
      return state;
    }

    case RESIDENTS.DELETE_RESIDENT_BY_SOCKET: {
      const data = action.payload;
      if (state.residents[data.id].nodeId !== data.nodeId) return state;
      delete state.residents[data.id];
      const selected = [...state.settings.selectedResidents];
      if (selected.indexOf(data.id) !== -1)
        selected.splice(selected.indexOf(data.id), 1);
      const residentSelect = StorageHelper.instance.GetResident();
      if (residentSelect === data.id) {
        StorageHelper.instance.ClearResident();
      }
      return {
        ...state,
        residents: { ...state.residents },
        settings: { ...state.settings, selectedResidents: selected },
        isLoading: false,
      };
    }

    case RESIDENTS.GET_ONE_RESIDENT_SUCCESS: {
      const data = (action as IGetOneResidentSuccess).payload;
      let resident = state.residents[data.id];
      if (resident) {
        resident.birthdate = data.birthdate;
        resident.gender = data.gender;
      } else resident = data;

      return {
        ...state,
        residents: { ...state.residents, [resident.id]: resident },
        isLoading: false,
      };
    }

    case RESIDENTS.SET_SETTINGS: {
      const newSettings = (action as ISetResidentsSettings).payload;
      return { ...state, settings: { ...state.settings, ...newSettings } };
    }
    case RESIDENTS.CHANGE_SELECTED: {
      const selectChangeId = action.payload;
      const selected = [...state.settings.selectedResidents];
      if (selected.indexOf(selectChangeId) === -1)
        selected.push(selectChangeId);
      else selected.splice(selected.indexOf(selectChangeId), 1);
      return {
        ...state,
        settings: { ...state.settings, selectedResidents: selected },
      };
    }
    case RESIDENTS.DELETE_BATCH_DEVICE_SUCCESS: {
      const data: DeviceResidentDTO[] = (
        action as IDeleteBatchDevicesFromResidentsSuccess
      ).payload;

      data.map((wasDivorced) => {
        const currentResident = state.residents[wasDivorced?.residentId];
        if (currentResident) {
          currentResident.devices = currentResident.devices.filter(
            (id) => id !== wasDivorced?.deviceId
          );
          delete currentResident.devicesMinimized[wasDivorced?.deviceId];
        }
        return null;
      });
      return { ...state, residents: { ...state.residents }, isLoading: false };
    }
    case RESIDENTS.DELETE_MANY_RESIDENTS_SUCCESS: {
      const data = (action as IDeleteManyResidentsSuccess).payload;
      const selected = [...state.settings.selectedResidents];
      data.map((id) => {
        delete state.residents[id];
        if (selected.indexOf(id) !== -1)
          selected.splice(selected.indexOf(id), 1);
        return null;
      });
      return {
        ...state,
        residents: { ...state.residents },
        settings: { ...state.settings, selectedResidents: selected },
        isLoading: false,
      };
    }
    case RESIDENTS.BATCH_DISCONNECT_AND_CHANGE_SUCCESS: {
      const data = (action as IBatchDisconectAndChangeResidentsSuccess).payload;
      const mapedResidents = {} as ResidentMap;
      data.map((wasChanged) => {
        const resident = state.residents[wasChanged.id];
        if (resident) {
          mapedResidents[resident.id] = {
            ...resident,
            devices: [],
            devicesMinimized: {},
            nodeId: wasChanged.resourceNodeId,
          };
        }
        return null;
      });

      return {
        ...state,
        residents: { ...state.residents, ...mapedResidents },
        isLoading: false,
      };
    }
    case RESIDENTS.MOVE_RESIDENT_WITH_DEVICES_SUCCESS: {
      const data = action.payload;
      const mapedResidents = {} as ResidentMap;
      if (Array.isArray(data)) {
        data.map((el) => {
          const currResident = state.residents[el.id];
          if (currResident && currResident.nodeId !== el.resourceNodeId) {
            mapedResidents[currResident.id] = {
              ...currResident,
              nodeId: el.resourceNodeId,
            };
          }
          return null;
        });
        if (Object.keys(mapedResidents).length > 0)
          return {
            ...state,
            residents: { ...state.residents, ...mapedResidents },
            isLoading: false,
          };
      }
      return { ...state, isLoading: false };
    }

    case RESIDENTS.DELETE_DEVICE_FROM_RESIDENT_BYSOCKET: {
      const data = action.payload as DeviceResidentDTO;

      const currentResident = state.residents[data?.residentId];
      if (currentResident) {
        delete currentResident.devicesMinimized[data.deviceId];
        return {
          ...state,
          residents: {
            ...state.residents,
            [currentResident.id]: {
              ...currentResident,
              devices: [
                ...currentResident.devices.filter((id) => id !== data.deviceId),
              ],
              devicesMinimized: { ...currentResident.devicesMinimized },
            },
          },
        };
      }
      return state;
    }
    case RESIDENTS.ADD_DEVICE_TO_RESIDENT_BY_SOCKET: {
      const data: ADDDeviceResidentDTO = (
        action as IAddDeviceToResidentBySocket
      ).payload;
      const currentResident = state.residents[data?.residentId];
      if (currentResident) {
        const newDevices =
          currentResident.devices.findIndex((el) => el === data.deviceId) === -1
            ? [...currentResident.devices, data.deviceId]
            : currentResident.devices;
        const newMini = {
          ...currentResident.devicesMinimized,
          [data.deviceId]: {
            type: data?.deviceType as DeviceType,
            room: data?.room || "",
            bed: data?.bed || "",
          },
        };
        return {
          ...state,
          residents: {
            ...state.residents,
            [currentResident.id]: {
              ...currentResident,
              devicesMinimized: newMini,
              devices: newDevices,
            },
          },
        };
      }
      return state;
    }
    case RESIDENTS.MOVE_RESIDENT_WITH_DEVICES_BY_SOCKET: {
      const data = action.payload as MoveResidentWithDevicesBySocketDTO;
      const currentResident = state.residents[data?.residentId];
      if (currentResident && currentResident.nodeId !== data.toNodeId) {
        return {
          ...state,
          residents: {
            ...state.residents,
            [currentResident.id]: { ...currentResident, nodeId: data.toNodeId },
          },
        };
      }
      return state;
    }
    case RESIDENTS.GET_RESIDENTS_PICTURES_SUCCESS: {
      const data = action.payload;
      const updatedResidents = {};
      data.map((el) => {
        if (el?.picture === null) return null;
        const was = state.residents[el?.self?.id]
          ? state.residents[el?.self?.id]
          : IMAGE_STUB;
        updatedResidents[el?.self?.id] = {
          ...was,
          picture: el?.picture,
          id: el?.self?.id,
        };
        return null;
      });
      if (Object.keys(updatedResidents).length > 0) {
        return {
          ...state,
          residents: {
            ...state.residents,
            ...updatedResidents,
          },
        };
      }
      return state;
    }

    case RESIDENTS.GET_RESIDENTS_REQUEST: {
      const { blocking } = action.payload;
      return blocking ? { ...state, isLoading: true } : state;
    }
    case RESIDENTS.CHANGE_NODE_PRESENCE_BY_SOCKET: {
      const { nodePresenceType, residentIds } = action.payload;
      const mapped: ResidentMap = {};
      residentIds.map((id) => {
        const wasres = state.residents[id];
        if (wasres)
          mapped[id] = {
            ...wasres,
            nodePresence: nodePresenceType,
            lastUpdate: new Date().toISOString(),
          };
        return null;
      });
      return { ...state, residents: { ...state.residents, ...mapped } };
    }
    case RESIDENTS.ADD_RESIDENT_REQUEST:
    case RESIDENTS.GET_ONE_RESIDENT_REQUEST:
    case RESIDENTS.DELETE_BATCH_DEVICE_REQUEST:
    case RESIDENTS.DELETE_MANY_RESIDENTS_REQUEST:
    case RESIDENTS.BATCH_DISCONNECT_AND_CHANGE_REQUEST: {
      return { ...state, isLoading: true };
    }
    case RESIDENTS.MOVE_RESIDENT_WITH_DEVICES_FAILURE:
    case RESIDENTS.GET_RESIDENTS_FAILURE:
    case RESIDENTS.ADD_RESIDENT_FAILURE:
    case RESIDENTS.ADD_RESIDENT_SUCCESS:
    case RESIDENTS.GET_ONE_RESIDENT_FAILURE:
    case RESIDENTS.GET_RULE_FAILURE:
    case RESIDENTS.DELETE_BATCH_DEVICE_FAILURE:
    case ALERTS.GET_ALERTS_FROM_RESIDENT_SUCCESS:
    case ALERTS.GET_ALERTS_FROM_RESIDENT_FAILURE:
    case RESIDENTS.BATCH_DISCONNECT_AND_CHANGE_FAILURE: {
      return { ...state, isLoading: false };
    }
    case RESIDENTS.GET_RESIDENT_RULES_SUCCESS:
    case RESIDENTS.GET_RULE_SUCCESS: {
      return { ...state, isLoading: false };
    }
    default:
      return state;
  }
};

const parseDevices = (devices: any = []) => {
  return devices.map((devicePro: any) => {
    const { self } = devicePro;
    return self.id;
  });
};

const parseDevicesMap = (devices: any = []) => {
  const map = {};
  devices.forEach((element) => {
    const { self, type, room, bed } = element;
    map[self.id] = {
      type:
        type.toLowerCase() === DeviceType.vitals
          ? DeviceType.vitals
          : type.toLowerCase() === DeviceType.biopatch
          ? DeviceType.biopatch
          : DeviceType.watch,
      room: room === null ? undefined : room,
      bed: bed === null ? undefined : bed,
    };
  });
  return map;
};

const vitalsFirst = (a: any, b: any) => {
  if (a?.type === DeviceType.vitals) return -1;
  return 0;
};

export default reducer;
